import { useEffect, useRef, useCallback } from 'react';

// Vite substitutes import.meta.env.VITE_* at build/serve time. Reading
// process.env.SESSION_TIMEOUT_MINUTES via the define block was unreliable
// (cache / config-load order issues), so use the VITE_-prefixed var instead.
const TIMEOUT_MINUTES_RAW = import.meta.env?.VITE_SESSION_TIMEOUT_MINUTES;
const IDLE_TIMEOUT_MS = (Number(TIMEOUT_MINUTES_RAW) || 10) * 60 * 1000;
// eslint-disable-next-line no-console
console.log(
    '[IDLE_TIMEOUT] configured:',
    IDLE_TIMEOUT_MS / 60000,
    'minutes (raw env:',
    TIMEOUT_MINUTES_RAW,
    ')'
);
// eslint-disable-next-line no-undef
const ACTIVITY_EVENTS: (keyof GlobalEventHandlersEventMap)[] = [
    'mousedown',
    'mousemove',
    'keydown',
    'scroll',
    'touchstart',
    'click'
];

// Cross-tab activity sharing key (localStorage-based for broad browser support)
const ACTIVITY_KEY = 'taytan_last_activity';

export default function useIdleTimeout(onIdle: () => void, active: boolean) {
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const onIdleRef = useRef(onIdle);
    onIdleRef.current = onIdle;

    const resetTimer = useCallback(() => {
        if (timerRef.current) clearTimeout(timerRef.current);
        const now = Date.now();
        // Broadcast activity to other tabs via localStorage
        try {
            localStorage.setItem(ACTIVITY_KEY, now.toString());
        } catch {
            // ignore storage errors
        }
        timerRef.current = setTimeout(() => {
            const fireTime = Date.now();
            const timerAge = fireTime - now;
            // Before firing idle, check if another tab was active recently
            try {
                const lastActivity = Number(localStorage.getItem(ACTIVITY_KEY) || '0');
                const elapsed = fireTime - lastActivity;
                if (elapsed < IDLE_TIMEOUT_MS) {
                    // Another tab was active — reschedule instead of firing idle
                    const remaining = IDLE_TIMEOUT_MS - elapsed;
                    // eslint-disable-next-line no-console
                    console.log(
                        '[IDLE_TIMEOUT] cross-tab activity detected, rescheduling for',
                        Math.round(remaining / 60000),
                        'min'
                    );
                    timerRef.current = setTimeout(() => onIdleRef.current(), remaining);
                    return;
                }
                // eslint-disable-next-line no-console
                console.warn(
                    '[IDLE_TIMEOUT] firing logout now. Timer ran for',
                    Math.round(timerAge / 60000),
                    'min. Last activity was',
                    Math.round(elapsed / 60000),
                    'min ago. Timeout is',
                    IDLE_TIMEOUT_MS / 60000,
                    'min'
                );
            } catch {
                // eslint-disable-next-line no-console
                console.warn(
                    '[IDLE_TIMEOUT] firing logout (localStorage error). Timer ran for',
                    Math.round(timerAge / 60000),
                    'min'
                );
            }
            onIdleRef.current();
        }, IDLE_TIMEOUT_MS);
    }, []);

    useEffect(() => {
        if (!active) return;

        resetTimer();

        for (const event of ACTIVITY_EVENTS) {
            document.addEventListener(event, resetTimer, { passive: true });
        }

        // Listen for activity from other tabs
        const onStorage = (e: StorageEvent) => {
            if (e.key === ACTIVITY_KEY && e.newValue) {
                resetTimer();
            }
        };
        window.addEventListener('storage', onStorage);

        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
            for (const event of ACTIVITY_EVENTS) {
                document.removeEventListener(event, resetTimer);
            }
            window.removeEventListener('storage', onStorage);
        };
    }, [active, resetTimer]);
}
